import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

/**
 * Typed data-access layer for the `/tipers` endpoints.
 *
 * Centralises query keys, URL building, the response envelope and cache
 * invalidation that were hand-rolled inline across TipersPage, TiperDetailPage
 * and TiperFormModal. Mutations own the URL + envelope + invalidation; page
 * reactions (toasts, navigation, modal close, form resets) stay at the call site
 * via `mutate(vars, { onSuccess, onError })`. Keys preserved verbatim:
 *   - list   → ['tipers', search, branchId]   (broad prefix ['tipers'])
 *   - detail → ['tiper-detail', id]
 */

export interface InvoiceProfile {
  iban?: string | null
  ico?: string | null
  dic?: string | null
  icDph?: string | null
  registrationOffice?: string | null
  registrationNumber?: string | null
  billingStreet?: string | null
  billingCity?: string | null
  billingZip?: string | null
  billingCountry?: string | null
  organization?: string | null
  isCompany?: boolean
  companyName?: string | null
}

export interface Tiper {
  id: string
  firstName: string
  lastName: string
  email: string
  phone?: string | null
  instagramUrl?: string | null
  facebookUrl?: string | null
  branchId?: string | null
  branch?: { id: string; name: string } | null
  invoiceProfile?: InvoiceProfile | null
}

export interface TiperListItem extends Tiper {
  createdAt: string
}

export interface TiperDetail {
  id: string
  firstName: string
  lastName: string
  email: string
  phone: string | null
  instagramUrl: string | null
  facebookUrl: string | null
  branchId: string | null
  branch: { id: string; name: string; city?: string | null } | null
  status: string
  createdAt: string
  updatedAt: string
  invoiceProfile: {
    iban: string | null
    ico: string | null
    dic: string | null
    icDph: string | null
    registrationOffice: string | null
    registrationNumber: string | null
    billingStreet: string | null
    billingCity: string | null
    billingZip: string | null
    billingCountry: string
    organization?: string | null
    isCompany: boolean
    companyName: string | null
  } | null
}

export interface TiperListFilters {
  search?: string
  branchId?: string
}

export const tiperKeys = {
  /** Prefix matching every tiper list — use for broad invalidation. */
  all: ['tipers'] as const,
  list: (filters: TiperListFilters) =>
    ['tipers', filters.search ?? '', filters.branchId ?? ''] as const,
  detail: (id: string | undefined) => ['tiper-detail', id] as const,
}

/** Tiper list (GET /tipers) with optional search + branch filter. */
export function useTipers(filters: TiperListFilters) {
  return useQuery<TiperListItem[]>({
    queryKey: tiperKeys.list(filters),
    queryFn: () =>
      api
        .get('/tipers', {
          params: {
            search: filters.search || undefined,
            branchId: filters.branchId || undefined,
          },
        })
        .then((r) => r.data),
  })
}

/** Single tiper detail (GET /tipers/{id}). */
export function useTiper(id: string | undefined) {
  return useQuery<TiperDetail>({
    queryKey: tiperKeys.detail(id),
    queryFn: () => api.get(`/tipers/${id}`).then((r) => r.data),
    enabled: !!id,
  })
}

/** Delete a tiper (DELETE /tipers/{id}). Invalidates the list. */
export function useDeleteTiper() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/tipers/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: tiperKeys.all }),
  })
}

/** Patch a tiper (PATCH /tipers/{id}). Invalidates detail + list. Caller builds
 * the payload (the detail form maps its fields) and shows the error inline. */
export function useUpdateTiper(id: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => api.patch(`/tipers/${id}`, payload, { skipErrorToast: true }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: tiperKeys.detail(id) })
      qc.invalidateQueries({ queryKey: tiperKeys.all })
    },
  })
}

/** Create-or-update a tiper from the form modal (POST or PATCH). Returns the
 * saved Tiper. Invalidates the list. The modal shows the error inline. */
export function useSaveTiper(tiperId?: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (values: unknown) =>
      (tiperId
        ? api.patch(`/tipers/${tiperId}`, values, { skipErrorToast: true })
        : api.post('/tipers', values, { skipErrorToast: true })
      ).then((r) => r.data as Tiper),
    onSuccess: () => qc.invalidateQueries({ queryKey: tiperKeys.all }),
  })
}
